import { DeleteOutlined, EditOutlined, LinkOutlined, PlusOutlined } from '@ant-design/icons';
import {
  OutboundReferenceCollection,
  ProductSupplierDoc,
  PurchaseOrderSectionConfigDoc,
} from '@gooduncles/gu-app-schema';
import { ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { Button, Popconfirm, Space, Switch, Tag, Typography, message } from 'antd';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { formatDateForAgCell } from 'src/utils/ag-grid-util';
import { errorObjectToString } from 'src/utils/common';
import {
  deletePurchaseOrderSectionConfig,
  observeProductSupplier,
  observePurchaseOrderSectionConfig,
  updatePurchaseOrderSectionConfig,
} from 'src/utils/firebase/firebase-short-cut';

import InventoryLinkModal from 'src/atomic-components/organisms/Modals/InventoryLinkModal';
import PurchaseOrderSectionConfigModal from 'src/atomic-components/organisms/Modals/PurchaseOrderSectionConfigModal';

const { Title } = Typography;

const defaultColDef = {
  sortable: true,
  resizable: true,
  filter: true,
};

/** OutboundReferenceCollection 렌더러 */
const ReferenceCollectionRenderer = ({ value }: { value: OutboundReferenceCollection }) => {
  switch (value) {
    case OutboundReferenceCollection.ORDER:
      return <Tag color='blue'>주문</Tag>;
    case OutboundReferenceCollection.ISSUE_TASK:
      return <Tag color='orange'>이슈</Tag>;
    default:
      return <Tag>{value}</Tag>;
  }
};

const PreOrderProductPage: FC = () => {
  const [sectionConfigs, setSectionConfigs] = useState<PurchaseOrderSectionConfigDoc[]>([]);
  const [productSuppliers, setProductSuppliers] = useState<ProductSupplierDoc[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<PurchaseOrderSectionConfigDoc | undefined>(undefined);
  const [isInventoryModalOpen, setIsInventoryModalOpen] = useState(false);
  const [inventoryEditConfig, setInventoryEditConfig] = useState<PurchaseOrderSectionConfigDoc | null>(null);

  // 데이터 로드
  useEffect(() => {
    const sectionConfig$ = observePurchaseOrderSectionConfig();
    const subscription = sectionConfig$.subscribe(setSectionConfigs);
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const supplier$ = observeProductSupplier([['deletedAt', '==', null]]);
    const subscription = supplier$.subscribe(setProductSuppliers);
    return () => subscription.unsubscribe();
  }, []);

  // 매입처 ID -> 이름 맵
  const supplierNameMap = useMemo(() => {
    const map = new Map<string, string>();
    productSuppliers.forEach((supplier) => {
      map.set(supplier._id, supplier.name);
    });
    return map;
  }, [productSuppliers]);

  // 정렬된 데이터: 매입처 이름순 > 정렬순서
  const sortedSectionConfigs = useMemo(() => {
    return [...sectionConfigs].sort((a, b) => {
      const supplierNameA = supplierNameMap.get(a.supplierId) ?? '';
      const supplierNameB = supplierNameMap.get(b.supplierId) ?? '';
      const nameCompare = supplierNameA.localeCompare(supplierNameB);
      if (nameCompare !== 0) return nameCompare;
      return a.sortOrder - b.sortOrder;
    });
  }, [sectionConfigs, supplierNameMap]);

  const handleOpenCreateModal = () => {
    setEditingConfig(undefined);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (data: PurchaseOrderSectionConfigDoc) => {
    setEditingConfig(data);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingConfig(undefined);
  };

  const handleOpenInventoryModal = (data: PurchaseOrderSectionConfigDoc) => {
    setInventoryEditConfig(data);
    setIsInventoryModalOpen(true);
  };

  const handleCloseInventoryModal = () => {
    setIsInventoryModalOpen(false);
    setInventoryEditConfig(null);
  };

  const handleToggleEnabled = useCallback(async (id: string, enabled: boolean) => {
    try {
      await updatePurchaseOrderSectionConfig(id, { enabled });
      message.success('활성화 상태가 변경되었습니다.');
    } catch (error) {
      const errorMessage = errorObjectToString(error);
      message.error(errorMessage);
    }
  }, []);

  const handleDelete = useCallback(async (id: string) => {
    try {
      await deletePurchaseOrderSectionConfig(id);
      message.success('설정이 삭제되었습니다.');
    } catch (error) {
      const errorMessage = errorObjectToString(error);
      message.error(errorMessage);
    }
  }, []);

  const columnDefs: ColDef<PurchaseOrderSectionConfigDoc>[] = useMemo(
    () => [
      {
        headerName: 'ID',
        field: '_id',
        hide: true,
      },
      {
        headerName: '매입처',
        field: 'supplierId',
        valueFormatter: (params) => supplierNameMap.get(params.value) ?? params.value,
        width: 150,
      },
      {
        headerName: '출처 컬렉션',
        field: 'referenceCollection',
        cellRenderer: (params: { value: OutboundReferenceCollection }) => (
          <ReferenceCollectionRenderer value={params.value} />
        ),
        width: 120,
      },
      {
        headerName: '섹션 헤더',
        field: 'sectionHeader',
        flex: 1,
        minWidth: 200,
      },
      {
        headerName: '정렬 순서',
        field: 'sortOrder',
        width: 120,
      },
      {
        headerName: '연결된 재고',
        field: 'inventoryIds',
        width: 140,
        cellRenderer: (params: { data: PurchaseOrderSectionConfigDoc }) => (
          <Button
            type='link'
            size='small'
            icon={<LinkOutlined />}
            onClick={() => handleOpenInventoryModal(params.data)}>
            {params.data.inventoryIds?.length ?? 0}개
          </Button>
        ),
      },
      {
        headerName: '설명',
        field: 'description',
        width: 200,
      },
      {
        headerName: '활성화',
        field: 'enabled',
        width: 100,
        cellRenderer: (params: { data: PurchaseOrderSectionConfigDoc }) => (
          <Switch
            size='small'
            checked={params.data.enabled}
            onChange={(checked) => handleToggleEnabled(params.data._id, checked)}
          />
        ),
      },
      {
        headerName: '생성일',
        field: '_timeCreate',
        hide: true,
        valueFormatter: formatDateForAgCell('yyyy-MM-dd HH:mm'),
        width: 140,
      },
      {
        headerName: '액션',
        width: 120,
        cellRenderer: (params: { data: PurchaseOrderSectionConfigDoc }) => (
          <Space size='small' style={{ columnGap: 10 }}>
            <Button type='text' icon={<EditOutlined />} onClick={() => handleOpenEditModal(params.data)} />
            <Popconfirm
              title='설정 삭제'
              description='이 설정을 삭제하시겠습니까?'
              onConfirm={() => handleDelete(params.data._id)}
              okText='삭제'
              cancelText='취소'>
              <Button type='text' danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [supplierNameMap, handleToggleEnabled, handleDelete]
  );

  return (
    <div className='ag-theme-alpine height100 flexColumn'>
      <div className='flexRow flexSpaceBetween' style={{ marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          선주문 상품 관리
        </Title>
        <Space>
          <Button type='primary' icon={<PlusOutlined />} onClick={handleOpenCreateModal}>
            설정 추가
          </Button>
        </Space>
      </div>
      <AgGridReact
        rowData={sortedSectionConfigs}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        enableCellTextSelection={true}
        tooltipShowDelay={500}
      />
      <PurchaseOrderSectionConfigModal open={isModalOpen} onClose={handleCloseModal} initialData={editingConfig} />
      <InventoryLinkModal
        open={isInventoryModalOpen}
        onClose={handleCloseInventoryModal}
        config={inventoryEditConfig}
      />
    </div>
  );
};

export default PreOrderProductPage;
